/**
 * Document utility functions for file operations
 */

import {
  FileText,
  Image,
  Briefcase,
  Home,
  Shield,
  Gavel,
  Mail,
  CreditCard,
  Receipt,
  FileQuestion,
  ScrollText,
  Heart,
  type LucideIcon
} from 'lucide-react';

/**
 * Format bytes to human readable file size
 */
export function formatFileSize(bytes: number): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  return filename
    .slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2)
    .toLowerCase();
}

/**
 * Detect file type category from filename
 */
export function detectFileTypeCategory(filename: string): string {
  const extension = getFileExtension(filename);

  // Image files
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(extension)) {
    return 'image';
  }

  // PDF files
  if (extension === 'pdf') {
    return 'pdf';
  }

  // Document files
  if (['doc', 'docx', 'txt', 'rtf', 'odt'].includes(extension)) {
    return 'document';
  }

  // Spreadsheet files
  if (['xls', 'xlsx', 'csv', 'ods'].includes(extension)) {
    return 'spreadsheet';
  }

  return 'other';
}

/**
 * Get appropriate icon for file type
 * Death certificates and wills get special icons to highlight their importance
 */
export function getFileIcon(fileType: string): LucideIcon {
  const iconMap: Record<string, LucideIcon> = {
    // Uppercase with underscores (old format)
    WILL: ScrollText, // Distinctive scroll icon for wills
    DEATH_CERTIFICATE: Heart, // Heart icon to represent life/death documents
    PROBATE_APPLICATION: Briefcase,
    PROPERTY_DOCUMENT: Home,
    SHARE_CERTIFICATE: Receipt,
    INSURANCE_DOCUMENT: Shield,
    COURT_DOCUMENT: Gavel,
    CORRESPONDENCE: Mail,
    IDENTIFICATION: CreditCard,
    STATEMENT: FileText,
    APPLICATION_FORM: FileText,
    OTHER: FileQuestion,
    // Lowercase with hyphens (new format)
    will: ScrollText, // Distinctive scroll icon for wills
    'death-certificate': Heart, // Heart icon to represent life/death documents
    'grant-of-probate': Briefcase,
    'letter-of-administration': Briefcase,
    identification: CreditCard,
    'bank-statement': FileText,
    'property-title': Home,
    'insurance-policy': Shield,
    'share-certificate': Receipt,
    other: FileQuestion,
    // Add category-based fallbacks
    image: Image,
    pdf: FileText,
    document: FileText,
    spreadsheet: FileText
  };

  return iconMap[fileType] || FileQuestion;
}

/**
 * Get human-readable label for file type
 */
export function getFileTypeLabel(fileType: string): string {
  const labelMap: Record<string, string> = {
    // Uppercase with underscores (old format)
    WILL: 'Will',
    DEATH_CERTIFICATE: 'Death Certificate',
    PROBATE_APPLICATION: 'Probate Application',
    PROPERTY_DOCUMENT: 'Property Document',
    SHARE_CERTIFICATE: 'Share Certificate',
    INSURANCE_DOCUMENT: 'Insurance Document',
    COURT_DOCUMENT: 'Court Document',
    CORRESPONDENCE: 'Correspondence',
    IDENTIFICATION: 'Identification',
    STATEMENT: 'Statement',
    APPLICATION_FORM: 'Application Form',
    OTHER: 'Other',
    // Lowercase with hyphens (new format)
    will: 'Will',
    'death-certificate': 'Death Certificate',
    'grant-of-probate': 'Grant of Probate',
    'letter-of-administration': 'Letter of Administration',
    identification: 'Identification',
    'bank-statement': 'Bank Statement',
    'property-title': 'Property Title',
    'insurance-policy': 'Insurance Policy',
    'share-certificate': 'Share Certificate',
    other: 'Other'
  };

  return labelMap[fileType] || fileType;
}

/**
 * Validate file for upload
 */
export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

export function validateFileForUpload(
  file: File,
  maxSizeBytes: number = 20 * 1024 * 1024 // 20MB default
): FileValidationResult {
  // Check file size
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size exceeds maximum allowed size of ${formatFileSize(maxSizeBytes)}`
    };
  }

  // Check if file has content
  if (file.size === 0) {
    return {
      valid: false,
      error: 'File is empty'
    };
  }

  // Add more validation rules as needed

  return { valid: true };
}

/**
 * Sort documents by various criteria
 */
export type DocumentSortField = 'name' | 'uploadedAt' | 'fileType' | 'size';
export type DocumentSortOrder = 'asc' | 'desc';

export function sortDocuments<
  T extends {
    originalFilename?: string;
    uploadedAt?: string;
    fileType?: string;
    sizeBytes?: number;
  }
>(
  documents: T[],
  field: DocumentSortField = 'uploadedAt',
  order: DocumentSortOrder = 'desc'
): T[] {
  return [...documents].sort((a, b) => {
    let compareValue = 0;

    switch (field) {
      case 'name':
        compareValue = (a.originalFilename || '').localeCompare(
          b.originalFilename || ''
        );
        break;
      case 'uploadedAt':
        compareValue =
          new Date(a.uploadedAt || 0).getTime() -
          new Date(b.uploadedAt || 0).getTime();
        break;
      case 'fileType':
        compareValue = (a.fileType || '').localeCompare(b.fileType || '');
        break;
      case 'size':
        compareValue = (a.sizeBytes || 0) - (b.sizeBytes || 0);
        break;
    }

    return order === 'asc' ? compareValue : -compareValue;
  });
}

/**
 * Filter documents by search query
 */
export function filterDocumentsBySearch<
  T extends {
    originalFilename?: string;
    fileType?: string;
  }
>(documents: T[], searchQuery: string): T[] {
  if (!searchQuery.trim()) return documents;

  const query = searchQuery.toLowerCase();
  return documents.filter((doc) => {
    const filename = (doc.originalFilename || '').toLowerCase();
    const fileType = getFileTypeLabel(doc.fileType || '').toLowerCase();

    return filename.includes(query) || fileType.includes(query);
  });
}
